import { type CSSProperties, type Dispatch, type ReactNode, type SetStateAction } from "react";
import type {
  GeneratedNodeAttribute,
  GeneratedStatus,
  InstanceDetail,
  NotificationSubscriptionDto,
  SetNodeAttributeRequest,
} from "@keydials/api-client";
import { HELP_ANCHORS, RichText, richTextHasContent } from "@keydials/ui";
import { sectionTitle } from "../dialModel";
import type { DialVM, VSection, VSegment } from "../dialModel";
import { DEFAULT_SEGMENT_NOUN, DEFAULT_ITEM_NOUN, dialLabel, itemNouns, sectionSegmentNoun, segmentNoun, listTabLabel, type ItemNoun, type SegmentNoun } from "../vocabulary";
import { Icon } from "../components/Icon";
import { TabStrip, type TabStripItem } from "../components/TabStrip";
import { useTabStripDisplay } from "../context";
import { Avatar, AvatarStack, StatusBadge, StatusDot } from "../components/primitives";
import { SubscribeButton } from "./SubscribeButton";
import { StatusPicker } from "./StatusPicker";
import { InstanceFilesPanel } from "./InstanceFilesPanel";
import { ActivityPanel } from "./ActivityPanel";
import { ComingUpPanel, ComingUpStrip } from "./ComingUpPanel";
import { LookBackPanel } from "./LookBackPanel";
import { COMPARE_TAB_IDS, withCompareTabs } from "./compare/compareTabs";
import { ResetsStrip } from "./ResetsStrip";
import { useRememberedTab } from "./rememberedTabs";
import { ScopeDiscussionTranscript } from "./ScopeDiscussionTranscript";
import { transcriptTitle } from "./transcriptExport";
import { VideoPlayer, GuidanceVideos } from "../components/GuidanceVideos";
import { GuidanceDocuments } from "../components/GuidanceDocuments";
import { ExternalResources, Prose, SectionAttributesBlock } from "./ActionPointPanel";
import { requirementHint, segmentIsLocked } from "./segmentRequirements";
import { legendScales } from "./legendScales";
import { HiddenTag } from "./HiddenTag";
import { fmtDate, hexA, personFrom, RAG_COLOUR, RAG_LABEL, ragLabel } from "../lib/ui";
import { sectionBandFill } from "@keydials/dial-renderer";

/**
 * Whose work a section is, when another organisation feeds it onto this dial (KEY-257 phase 3).
 *
 * Not decoration: two dials built from the same definition have the same section names, so without this
 * a homeowner watching Building Control's "Structure" beside their own "Structure" has no way to tell
 * them apart — and the fed one is read-only and counts towards somebody else's compliance figure.
 */
export function FedTag({ fedFrom }: { fedFrom?: { organisationName: string; dialName: string } | null }) {
  if (!fedFrom) {
    return null;
  }
  return (
    <span
      title={`Fed to you by ${fedFrom.organisationName} from “${fedFrom.dialName}”`}
      style={{
        flex: "none",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.3,
        padding: "2px 7px",
        borderRadius: 99,
        border: "var(--border-w) solid var(--border)",
        background: "var(--surface-2)",
        color: "var(--text-muted)",
        maxWidth: 170,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      From {fedFrom.organisationName}
    </span>
  );
}

/** A tiny "Custom" / "Edited" tag for a node carrying the customisation overlay (Phase D). */
export function NodeTag({ origin, overridden }: { origin?: string; overridden?: boolean }) {
  const tag = origin === "Custom" ? { label: "Custom", colour: "var(--brand-600, #4338ca)" } : overridden ? { label: "Edited", colour: "var(--st-amber, #b45309)" } : null;
  if (!tag) {
    return null;
  }
  return (
    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: tag.colour, background: "var(--surface-2)", border: "var(--border-w) solid var(--border)", borderRadius: 99, padding: "1px 7px", whiteSpace: "nowrap" }}>
      {tag.label}
    </span>
  );
}

/** Read-only RAG chip for an itemised segment (segment colour + "done / total items"). `items` is the section's
 *  word for its items (KEY-753) — "No appointments" rather than "No items" where the section names them. */
export function RagBadge({ segment, items = DEFAULT_ITEM_NOUN }: { segment: VSegment; items?: ItemNoun }) {
  const colour = segment.colour;
  // KEY-773: a slot holding only calendar events has items but no band — say how many are done, not "No …".
  const label = segment.band
    ? ragLabel(segment.band, items)
    : segment.total > 0 ? `${segment.done} of ${segment.total} ${items.many} complete` : `No ${items.many}`;
  return (
    <span
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 11px",
        borderRadius: 99,
        fontSize: 12.5,
        fontWeight: 700,
        // KEY-529, as StatusBadge: a fixed line-height so the pill is ONE height in every row it appears
        // in — inheriting the row's made the same badge taller beside a wrapping dial name than beside a
        // single-line one, so it never sat on the row's optical centre. `flex: none` so a long name pushes
        // it along rather than squashing a `nowrap` box out through its own border.
        lineHeight: 1,
        color: colour,
        background: hexA(colour, 0.13),
        border: `1px solid ${hexA(colour, 0.3)}`,
        whiteSpace: "nowrap",
        flex: "none",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 99, background: colour, flex: "none" }} />
      {segment.done} / {segment.total} {items.many}
    </span>
  );
}

/** Score + health-tier chip for a Group overview segment (KEY-162): band colour, tier label, score %. */
export function GroupScoreBadge({ segment }: { segment: VSegment }) {
  const colour = segment.colour;
  const pct = segment.score == null ? null : Math.round(segment.score * 100);
  // KEY-509: a wedge showing a child status is labelled with that status — the words the child uses —
  // and the health tier moves into the tooltip beside the score. Band-coloured wedges read as before.
  const label = segment.displayStatus?.name ?? segment.classification ?? "No data yet";
  const tier = segment.displayStatus && segment.classification ? `${segment.classification} · ` : "";
  return (
    <span
      title={pct == null ? "No status rolled up yet" : `${tier}Composite score ${pct}%`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 11px",
        borderRadius: 99,
        fontSize: 12.5,
        fontWeight: 700,
        // KEY-529, as StatusBadge: a fixed line-height so the pill is ONE height in every row it appears
        // in — inheriting the row's made the same badge taller beside a wrapping dial name than beside a
        // single-line one, so it never sat on the row's optical centre. `flex: none` so a long name pushes
        // it along rather than squashing a `nowrap` box out through its own border.
        lineHeight: 1,
        color: colour,
        background: hexA(colour, 0.13),
        border: `1px solid ${hexA(colour, 0.3)}`,
        whiteSpace: "nowrap",
        flex: "none",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 99, background: colour, flex: "none" }} />
      {label}
      {pct == null ? "" : ` · ${pct}%`}
    </span>
  );
}

/**
 * The legend under the dial: the scale the dial is actually coloured by.
 *
 * It used to say "None / Some / All complete" for every itemised dial regardless — three hard-coded
 * words with no data behind them — while the segments above were coloured by a display-rule ladder,
 * a rollup axis or a per-segment palette. `legendScales` picks the vocabulary really in use, and
 * narrows to one action point's own scale when one is chosen (KEY-495).
 */
export function Legend({
  vm,
  showProgress,
  selSegment,
  selSection,
}: {
  vm: DialVM;
  showProgress: boolean;
  /** The chosen action point, when there is one: the key then describes its scale, not the dial's. */
  selSegment?: VSegment | null;
  /** The chosen section, when no action point is: its edge's scale and the scales of what's inside it. */
  selSection?: VSection | null;
}) {
  const Item = ({ colour, label }: { colour: string; label: string }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-muted)", fontWeight: 500 }}>
      <span style={{ width: 9, height: 9, borderRadius: 99, background: colour }} />
      {label}
    </span>
  );

  const scales = legendScales(vm, selSegment, selSection);
  // KEY-527: on a GROUP the ring is not completion and never was — `done` counts the wedges whose rolled-up
  // band came out Green and `total` is simply how many wedges there are, so a group of dials nobody has
  // finished can read 100% "complete" the moment every child is healthy. Saying what it counts costs a
  // word; the bare "Completion" is a claim the number does not support. The dial's own noun (KEY-539)
  // carries through, so a dial that calls them judgements says judgements.
  const progressLabel = vm.isGroup ? `${segmentNoun(vm.detail).Many} at Green` : "Completion";

  return (
    // kd-legend is the hook the Dynamic workspace takes this out of flow with (KEY-532): in flow it counts
    // toward the dial column's height, so centring the column would put the DIAL above centre by half a
    // legend. The marginTop here is what that rule overrides.
    <div className="kd-legend" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 9, marginTop: 22 }}>
      {scales.map((scale, i) => (
        <div
          key={scale.title ?? i}
          style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", justifyContent: "center", alignItems: "center" }}
        >
          {scale.title && (
            <span style={{ fontSize: 12, color: "var(--text-faint)", fontWeight: 600 }}>{scale.title}:</span>
          )}
          {scale.kind === "rag"
            ? (["Red", "Amber", "Green"] as const).map((band) => (
                <Item key={band} colour={RAG_COLOUR[band]} label={RAG_LABEL[band]} />
              ))
            : scale.statuses.map((s) => <Item key={s.key} colour={s.colour} label={s.name} />)}
        </div>
      ))}
      {showProgress && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px 16px",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: 9,
            borderTop: "var(--border-w) solid var(--border)",
            width: "100%",
            maxWidth: 440,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-muted)", fontWeight: 500 }}>
            <span style={{ width: 12, height: 5, borderRadius: 99, background: RAG_COLOUR.Green }} />
            {progressLabel}
          </span>
        </div>
      )}
    </div>
  );
}

/** The overview right-panel switcher: a segmented pill (Direction B).
 *
 *  Canonical dial-level tab order — Overview · About · Guidance · Sections · Discussion · Files · Members ·
 *  Activity · Settings. Only the tabs that exist today are listed; the ordering is fixed so later arrivals slot in
 *  without a debate. About sits next to Overview because it answers the same question — what is this dial —
 *  with the dial's own authored fields rather than its description. Guidance (KEY-534) sits beside About for
 *  the same kind of reason and ahead of Sections: both are about the dial itself, while Sections is the
 *  working list. Discussion (KEY-559) follows the working list because it is a read of what was said inside
 *  it. Settings sits last on purpose: it is the only tab that changes the dial rather than reading it. */
export type PanelTab = "overview" | "about" | "guidance" | "sections" | "children" | "comingUp" | "lookBack" | "discussion" | "files" | "members" | "activity" | "settings" | "changes";


function PanelTabs({ tab, setTab, labels, showAbout, showChildren, childrenLabel, showComingUp, showLookBack, showDiscussion, showFiles, showMembers, showActivity, showSettings, compare = false }: { tab: PanelTab; setTab: (t: PanelTab) => void; /** KEY-670: the dial, for its own tab names. */ labels: Pick<InstanceDetail, "labelOverrides">; showAbout: boolean; showChildren: boolean; childrenLabel: string; showComingUp: boolean; showLookBack: boolean; showDiscussion: boolean; showFiles: boolean; showMembers: boolean; showActivity: boolean; showSettings: boolean; /** Look back's compare mode: the reduced set plus Changes. */ compare?: boolean }) {
  // KEY-670: each heading is the dial's own word where its definition renames the tab, at the DIAL level.
  // Sections/Subdials, Members and Settings are not renamable (see DialLabelKeys for why).
  const items: TabStripItem<PanelTab>[] = [
    { id: "overview", label: dialLabel(labels, "overview.dial"), icon: "gauge" },
    // KEY-485: the dial's own fields, which used to render under *every* tab rather than belonging to one.
    ...(showAbout ? [{ id: "about" as PanelTab, label: dialLabel(labels, "about"), icon: "info" as const }] : []),
    // KEY-534: always present, as at the section and the action point — so a reader always knows where to
    // look. No count on the label: a dial carries at most one guidance video.
    { id: "guidance", label: dialLabel(labels, "guidance.dial"), icon: "compass", helpAnchorId: HELP_ANCHORS.dialTabGuidance },
    // KEY-529: on a GROUP this slot lists the child dials instead of the group's own face sections. It
    // REPLACES rather than joins them: a group's sections are a presentation of its children, so offering
    // both would be the same information twice, filed under two words for it. The child list used to live
    // in a card below the panel, visible only under Overview and invisible from every other tab.
    showChildren ? { id: "children" as PanelTab, label: childrenLabel, icon: "layers" } : { id: "sections" as PanelTab, label: "Sections", icon: "layers" },
    // KEY-COMINGUP: everything dated, soonest first. Beside the working list because it IS the working
    // list, in date order. Not on a group: a group's dates belong to its children.
    ...(showComingUp ? [{ id: "comingUp" as PanelTab, label: "Coming up", icon: "calendar" as const }] : []),
    // KEY-LOOKBACK: the dial as it was, beside the working list — the same clock, turned the other way.
    ...(showLookBack ? [{ id: "lookBack" as PanelTab, label: "Look back", icon: "history" as const }] : []),
    // KEY-559: read-only — a transcript of everything said in the action points this reader can see.
    ...(showDiscussion ? [{ id: "discussion" as PanelTab, label: dialLabel(labels, "discussion.dial"), icon: "chat" as const, helpAnchorId: HELP_ANCHORS.dialTabDiscussion }] : []),
    ...(showFiles ? [{ id: "files" as PanelTab, label: dialLabel(labels, "files.dial"), icon: "clip" as const }] : []),
    // Canonical dial tab order (KEY-462): Overview · Sections · Files · Members · Activity.
    ...(showMembers ? [{ id: "members" as PanelTab, label: "Members", icon: "users" as const }] : []),
    ...(showActivity ? [{ id: "activity" as PanelTab, label: dialLabel(labels, "activity.dial"), icon: "activity" as const }] : []),
    ...(showSettings ? [{ id: "settings" as PanelTab, label: "Settings", icon: "gear" as const, helpAnchorId: HELP_ANCHORS.dialSettings }] : []),
  ];
  const display = useTabStripDisplay();
  return <TabStrip<PanelTab> items={compare ? withCompareTabs(items) : items} active={tab} onSelect={setTab} variant="pill" ariaLabel="Dial overview" {...display} />;
}

/** A compact on/off switch for the "hide empty action points" overview filter. Mirrors the upload-automation toggle. */
function EmptyToggle({ on, onChange, noun = DEFAULT_SEGMENT_NOUN }: { on: boolean; onChange: (next: boolean) => void; noun?: SegmentNoun }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      title={`Hide sections and ${noun.many} that have no items`}
      onClick={() => onChange(!on)}
      className="kd-focusable"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8, font: "inherit", fontSize: 12.5, fontWeight: 700,
        padding: "6px 12px", borderRadius: 99, cursor: "pointer", whiteSpace: "nowrap",
        border: `var(--border-w) solid ${on ? "var(--accent-border)" : "var(--border)"}`,
        background: on ? "var(--accent-soft)" : "var(--surface)",
        color: on ? "var(--accent-text)" : "var(--text-muted)",
      }}
    >
      <span style={{ position: "relative", width: 26, height: 15, borderRadius: 99, flex: "none", transition: "background 0.12s", background: on ? "var(--accent, #6366f1)" : "var(--border)" }}>
        <span style={{ position: "absolute", top: 2, left: on ? 13 : 2, width: 11, height: 11, borderRadius: 99, background: "#fff", transition: "left 0.12s" }} />
      </span>
      Hide empty
    </button>
  );
}

/** RAG rollup colour for a section, from its leaf-item tally (matches the handoff donut). */
function sectionRagColour(done: number, total: number): string {
  if (total === 0) {
    return "var(--st-none)";
  }
  if (done >= total) {
    return RAG_COLOUR.Green;
  }
  return done === 0 ? RAG_COLOUR.Red : RAG_COLOUR.Amber;
}

/** A completion donut: surface-inset track + a RAG arc from 12 o'clock, done/total in the centre. */
function CircleProgress({ value, colour, label, size = 50, stroke = 5.5 }: {
  value: number; // 0..1
  colour: string;
  label: string;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <span style={{ position: "relative", width: size, height: size, flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-inset)" strokeWidth={stroke} />
        {value > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={colour}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${(circ * value).toFixed(2)} ${circ.toFixed(2)}`}
            style={{ transition: "stroke-dasharray .5s ease" }}
          />
        )}
      </svg>
      <span style={{ position: "absolute", fontSize: size * 0.27, fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-ui)", fontVariantNumeric: "tabular-nums" }}>{label}</span>
    </span>
  );
}

/** Sections-tab ring card: a completion donut leads the header; expanding reveals the action-point rows. */
export function SectionRingCard({
  section,
  expanded,
  onToggle,
  onOpenSection,
  onOpenAp,
  instanceId,
  subs,
  onSubsChanged,
  meHasPhoneNumber,
  showSubscribe = false,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  section: VSection;
  expanded: boolean;
  onToggle: () => void;
  onOpenSection: () => void;
  onOpenAp: (segmentKey: string) => void;
  instanceId?: string;
  subs?: NotificationSubscriptionDto[];
  onSubsChanged?: () => void | Promise<void>;
  meHasPhoneNumber?: boolean;
  /** Icon-only subscribe control on the section card and each action-point row (KEY-461). Live mode only —
   *  the admin draft preview host never passes `instanceId`/`subs`/`onSubsChanged`, so this stays false
   *  there regardless of what's passed in. */
  showSubscribe?: boolean;
  /** This dial's word for an action point (KEY-539). This card takes a VSection rather than the detail, so
   *  the vocabulary arrives as a prop; omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  const pct = section.total ? section.done / section.total : 0;
  const colour = sectionRagColour(section.done, section.total);
  const canSubscribe = showSubscribe && !!instanceId && !!subs && !!onSubsChanged;
  return (
    <div className="kd-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: "var(--surface)", borderRadius: expanded ? "var(--radius-lg) var(--radius-lg) 0 0" : "var(--radius-lg)" }}>
        <button
          onClick={onToggle}
          className="kd-focusable"
          aria-expanded={expanded}
          style={{
            flex: 1,
            minWidth: 0,
            font: "inherit",
            cursor: "pointer",
            border: "none",
            background: "none",
            padding: 0,
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <CircleProgress value={pct} colour={colour} label={`${section.done}/${section.total}`} />
          <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              {/* The section's band colour (KEY-755), so the list and the dial agree on which section is which. */}
              <span style={{ width: 8, height: 8, borderRadius: 99, background: sectionBandFill(section.colour, "var(--ring-section)"), flex: "none" }} />
              <span title={section.label} style={{ flex: 1, color: "var(--text)", fontWeight: 700, fontSize: 15.5, fontFamily: "var(--font-display)", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {sectionTitle(section)}
              </span>
              <NodeTag origin={section.origin} overridden={section.overridden} />
              <HiddenTag hidden={section.isHidden} />
              <FedTag fedFrom={section.fedFrom} />
              {/* The section's own status (KEY-479), beside the name so the list says what the edge on the
                  dial says. */}
              {section.status && <StatusBadge colour={section.status.colour} label={section.status.name} />}
            </span>
            <span style={{ fontSize: 12.5, color: "var(--text-faint)", fontWeight: 600 }}>
              {/* KEY-539: a one/many PICK, not an appended "s" — this was the only site in the package that
                  derived a plural, and no such rule survives a real vocabulary ("matrix" → "matrices"). */}
              {section.segments.length} {section.segments.length === 1 ? noun.one : noun.many} · {Math.round(pct * 100)}% complete
            </span>
          </span>
        </button>
        {canSubscribe && (
          <SubscribeButton
            variant="icon"
            instanceId={instanceId!}
            scope={{ sectionKey: section.key, segmentKey: null }}
            subs={subs!}
            onChanged={onSubsChanged!}
            scopeLabel="this section"
            meHasPhoneNumber={meHasPhoneNumber}
            // Where the first-run tour shows that a subscription doesn't have to be all-or-nothing: this
            // bell is on screen at the dial level, so the tour can reach it without asking anyone to click.
            helpAnchorId={HELP_ANCHORS.sectionRowSubscribe}
          />
        )}
        <button
          onClick={onToggle}
          aria-label={expanded ? "Collapse section" : "Expand section"}
          style={{ flex: "none", border: "none", background: "none", cursor: "pointer", padding: 4, display: "flex" }}
        >
          <Icon name="chevDown" size={18} style={{ color: "var(--text-faint)", transition: "transform .25s ease", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }} />
        </button>
      </div>
      {expanded && (
        <div style={{ padding: "0 16px 16px", borderTop: "var(--border-w) solid var(--border)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {section.segments.map((seg) => (
              <div
                key={seg.key}
                className="kd-row-hover"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "11px 13px",
                  borderRadius: "var(--radius-sm)",
                  border: "var(--border-w) solid var(--border)",
                  background: "var(--surface-2)",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => onOpenAp(seg.key)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    font: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    border: "none",
                    background: "none",
                    padding: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    color: "var(--text)",
                  }}
                >
                  <StatusDot colour={seg.colour} size={9} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seg.name}</span>
                    <NodeTag origin={seg.origin} overridden={seg.overridden} />
                    {!section.isHidden && <HiddenTag hidden={seg.isHidden} />}
                  </span>
                  {seg.isGroupSegment ? (
                    <GroupScoreBadge segment={seg} />
                  ) : seg.itemActive ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-faint)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {seg.done}/{seg.total}
                    </span>
                  ) : null}
                </button>
                {canSubscribe && (
                  <SubscribeButton
                    variant="icon"
                    instanceId={instanceId!}
                    scope={{ sectionKey: section.key, segmentKey: seg.key }}
                    subs={subs!}
                    onChanged={onSubsChanged!}
                    scopeLabel={`this ${noun.one}`}
                    meHasPhoneNumber={meHasPhoneNumber}
                  />
                )}
                <button
                  onClick={() => onOpenAp(seg.key)}
                  aria-label={`Open ${seg.name}`}
                  style={{ flex: "none", border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex" }}
                >
                  <Icon name="chevRight" size={15} style={{ color: "var(--text-faint)" }} />
                </button>
              </div>
            ))}
            <button
              onClick={onOpenSection}
              className="kd-focusable"
              style={{
                alignSelf: "flex-start",
                marginTop: 2,
                font: "inherit",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--accent-text)",
                fontSize: 13,
                fontWeight: 700,
                padding: "2px 0",
              }}
            >
              Open section
              <Icon name="chevRight" size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Section drill panel: title + description + action-point rows with status/RAG badges. */
/**
 * The section's own status control (KEY-479), supplied by the workspace only when the dial offers section
 * statuses and the host can write one. Null hides it entirely — a dial without the feature shows nothing.
 */
export interface SectionStatusControls {
  statuses: GeneratedStatus[];
  canSet: (status: GeneratedStatus) => boolean;
  onPick: (statusKey: string | null) => void;
}

/** The panes {@link SectionPanel} offers. Kept next to {@link PanelTab} so the two tab vocabularies stay
 *  visibly separate — a section has no Members or Settings of its own. Activity (KEY-464) slotted in beside
 *  Files exactly where this array was structured to take it.
 *
 *  Guidance (KEY-542) sits second, directly after Overview, matching where the action point puts its own —
 *  so at both levels guidance is somewhere the reader can name, instead of being part of whatever Overview
 *  happened to hold. Overview is now strictly "what this is": description and the section's own fields.
 *
 *  Discussion (KEY-559) follows Segments, as it follows Sections at the dial: a read of what was said in the
 *  list just before it. */
type SectionTabId = "overview" | "guidance" | "segments" | "discussion" | "files" | "activity" | "changes";

export function SectionPanel({
  scopePill,
  instanceId,
  section,
  onOpenAp,
  subs,
  onSubsChanged,
  meHasPhoneNumber,
  sectionStatus,
  detail,
  canEditAttributes,
  attributeAccess,
  onSetNodeAttribute,
  canEditDocs,
  canManageDocVersions,
  showFilesTab = false,
  canDownloadDocs = true,
  showActivityTab = false,
  showDiscussionTab = false,
  meUserId = null,
  compareChanges,
  compareOverview,
}: {
  /** KEY-532: the level chip (WHOLE DIAL / SECTION / ACTION POINT), rendered beside this panel's
   * title. In the Dynamic workspace it lives HERE rather than in a strip of its own above the
   * cards — that strip was a card in its own right, so the projection's frame and beam landed on
   * it instead of on the panel they were describing. Omitted in Classic, which keeps the strip. */
  scopePill?: ReactNode;
  instanceId: string;
  section: VSection;
  /** Look back's compare mode is on: the strip shrinks to the compare set and this is the Changes tab. */
  compareChanges?: ReactNode;
  /** Look back's compare card (Then beside Now): shown in place of the Overview tab while comparing. */
  compareOverview?: ReactNode;
  onOpenAp: (segmentKey: string) => void;
  subs: NotificationSubscriptionDto[];
  onSubsChanged: () => void | Promise<void>;
  meHasPhoneNumber?: boolean;
  sectionStatus?: SectionStatusControls | null;
  /** The whole dial's detail — {@link SectionAttributesBlock} on the Overview tab needs it for the
   *  section's own field declarations and values (KEY-279). */
  detail: InstanceDetail;
  canEditAttributes: boolean;
  attributeAccess?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  onSetNodeAttribute: (request: SetNodeAttributeRequest) => void | Promise<void>;
  canEditDocs: boolean;
  canManageDocVersions: boolean;
  /** Show the Files tab (live mode only — see DialWorkspaceCapabilities.documents — AND, since KEY-537,
   * only where the dial lets this reader see that files exist at all). */
  showFilesTab?: boolean;
  /** Whether this reader may download files here (KEY-537). Per-row the truth is `canDownload` on the row
   * itself; this gates the whole-set ZIP buttons, which no single row owns. */
  canDownloadDocs?: boolean;
  /** Show the Activity tab (KEY-464) — live mode only, as at the dial: a draft preview has no past. */
  showActivityTab?: boolean;
  /** Show the read-only Discussion transcript (KEY-559) — live mode with messaging on only. */
  showDiscussionTab?: boolean;
  /** The reader, so a restricted message's audience line in that transcript can say "you". */
  meUserId?: string | null;
}) {
  // KEY-774: the tab last chosen on any section, so moving to the next one keeps the reader where they were.
  // Held outside this panel (see rememberedTabs) — the Dynamic workspace remounts it on every pick. The
  // fallback below still validates it against this section's own strip.
  const [remembered, setTab] = useRememberedTab<SectionTabId>("section");
  const tab: SectionTabId = remembered ?? "overview";
  // KEY-539: this dial's word for an action point, for the Files browser mounted below — or, where this section
  // names its own lists (KEY-762, "dentists"), the section's word.
  const noun = segmentNoun(detail, section.key);
  const ownNoun = sectionSegmentNoun(detail, section.key);

  // KEY-670: the section's headings read the dial's own words at the SECTION level.
  const tabs: TabStripItem<SectionTabId>[] = [
    { id: "overview", label: dialLabel(detail, "overview.section"), icon: "gauge" },
    // KEY-542: unconditional, as at the action point — a Guidance tab that appears only when there is
    // guidance can't tell a reader that there is none, so "is there any?" costs a hunt through Overview.
    // The video count is a badge, as at the action point, so it reads the same at both levels — and still
    // shows when the strip is drawn as icons.
    {
      id: "guidance",
      label: dialLabel(detail, "guidance.section"),
      icon: "compass",
      count: section.videos?.length ?? 0,
      helpAnchorId: HELP_ANCHORS.sectionTabGuidance,
    },
    // KEY-762: the list tab says what this section's lists are ("Dentists") where it names them.
    { id: "segments", label: ownNoun?.Many ?? "Segments", icon: "listOrdered" },
    ...(showDiscussionTab ? [{ id: "discussion" as SectionTabId, label: dialLabel(detail, "discussion.section"), icon: "chat" as const, helpAnchorId: HELP_ANCHORS.sectionTabDiscussion }] : []),
    ...(showFilesTab ? [{ id: "files" as SectionTabId, label: dialLabel(detail, "files.section"), icon: "clip" as const, helpAnchorId: HELP_ANCHORS.sectionTabFiles }] : []),
    ...(showActivityTab ? [{ id: "activity" as SectionTabId, label: dialLabel(detail, "activity.section"), icon: "activity" as const, helpAnchorId: HELP_ANCHORS.sectionTabActivity }] : []),
  ];
  const stripDisplay = useTabStripDisplay();
  const shownTabs = compareChanges ? withCompareTabs(tabs) : tabs;
  // A tab whose strip entry is gone would otherwise strand the reader on an empty pane.
  const activeTab: SectionTabId =
    !shownTabs.some((t) => t.id === tab)
    || (tab === "files" && !showFilesTab) || (tab === "activity" && !showActivityTab) || (tab === "discussion" && !showDiscussionTab)
      ? "overview"
      : tab;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap-stack)" }}>
      <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
        {/* KEY-573: the row wraps, and the chip-and-title group asks for 240px before it will share a line, so
            in a narrow column the status and Subscribe drop BELOW it. Unwrapped, they held their width and
            squeezed the title into a sliver, and the status pill slid over the "in <dial>" link by the chip.
            The action point's header does the same: its status sits on a row of its own. */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap", flex: "1 1 240px" }}>
            {scopePill}
            <h2 title={section.label} style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "var(--text)" }}>{sectionTitle(section)}</h2>
            <FedTag fedFrom={section.fedFrom} />
            <NodeTag origin={section.origin} overridden={section.overridden} />
            <HiddenTag hidden={section.isHidden} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
            {sectionStatus && (
              <StatusPicker
                statuses={sectionStatus.statuses}
                current={section.status}
                canSet={sectionStatus.canSet}
                onPick={(key) => sectionStatus.onPick(key)}
                onClear={() => sectionStatus.onPick(null)}
                emptyLabel="Section status"
                helpAnchor={HELP_ANCHORS.sectionStatus}
              />
            )}
            <SubscribeButton
              instanceId={instanceId}
              scope={{ sectionKey: section.key, segmentKey: null }}
              subs={subs}
              onChanged={onSubsChanged}
              scopeLabel="this section"
              meHasPhoneNumber={meHasPhoneNumber}
              helpAnchorId={HELP_ANCHORS.sectionSubscribe}
            />
          </div>
        </div>
        <TabStrip<SectionTabId>
          items={shownTabs}
          active={activeTab}
          onSelect={setTab}
          variant="underline"
          ariaLabel={`${sectionTitle(section)} panes`}
          style={{ marginTop: 16 }}
          {...stripDisplay}
        />
      </div>

      {/* The section's panes are cards of their own below the header card — unlike the dial and action point,
          whose Overview sits inside one — so Look back's card needs the same padded card, or it runs hard
          against the edge. */}
      {activeTab === "overview" && compareOverview && (
        <div className="kd-card" style={{ padding: "var(--pad-card)" }}>{compareOverview}</div>
      )}
      {activeTab === "overview" && !compareOverview && (
        <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 12 }}>
          {richTextHasContent(section.description) && (
            <RichText value={section.description} style={{ margin: 0, color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }} />
          )}
          {/* KEY-279: the section's own fields — used to be a sibling card below this whole panel, moved
              in here as part of KEY-460's tabbed section page. */}
          <SectionAttributesBlock
            detail={detail}
            sectionKey={section.key}
            canEdit={canEditAttributes}
            access={attributeAccess}
            onSet={onSetNodeAttribute}
            fileGrants={{ canUpload: canEditDocs, canManageVersions: canManageDocVersions }}
          />
        </div>
      )}

      {activeTab === "guidance" && (
        // KEY-542: section guidance used to live at the top of Overview, where it read as part of the
        // description rather than as the how-to it is. Same shape as the action point's Guidance tab —
        // prose, then the attachments that support it (KEY-460 part 1): videos/documents/links attached
        // to the section itself, not to any one action point.
        <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 14 }}>
          <Prose text={section.guidance ?? ""} fallback="No guidance added yet." muted />
          <GuidanceVideos videos={section.videos} />
          <GuidanceDocuments documents={section.documents} />
          <ExternalResources links={section.links} />
        </div>
      )}

      {activeTab === "segments" && (
        <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 9 }}>
          {section.segments.map((seg) => {
            // KEY-460: an action point nobody can work yet, said HERE — the list is where people choose
            // what to open, so a lock they only discover a click later is a wasted click and, worse,
            // reads as the app being broken rather than as something waiting to be done.
            const locked = segmentIsLocked(seg.missingRequiredFields ?? []);
            return (
            <button
              key={seg.key}
              onClick={() => onOpenAp(seg.key)}
              className="kd-row-hover"
              title={locked ? requirementHint(seg.missingRequiredFields ?? []) : undefined}
              style={{
                font: "inherit",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "13px 14px",
                borderRadius: "var(--radius-sm)",
                border: `var(--border-w) solid ${locked ? "var(--rag-red, #e7161b)" : "var(--border)"}`,
                background: locked ? hexA("#e7161b", 0.06) : "var(--surface-2)",
                cursor: "pointer",
                textAlign: "left",
                color: "var(--text)",
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seg.name}</span>
                <NodeTag origin={seg.origin} overridden={seg.overridden} />
                {locked && (
                  // The words as well as the colour: red alone says "bad", not "one field short".
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "none", fontSize: 12, fontWeight: 700, color: "var(--rag-red, #e7161b)" }}>
                    <Icon name="lock" size={12} />
                    {(seg.missingRequiredFields ?? []).length === 1 ? "1 field needed" : `${(seg.missingRequiredFields ?? []).length} fields needed`}
                  </span>
                )}
              </span>
              {seg.isGroupSegment ? (
                <GroupScoreBadge segment={seg} />
              ) : !seg.itemActive ? (
                // Its own status — a Simple dial's action point, or an itemised one with no items yet (KEY-259).
                <StatusBadge colour={seg.colour} label={seg.status?.name ?? "Not set"} />
              ) : (
                <RagBadge segment={seg} items={itemNouns(detail, section.key)} />
              )}
              <Icon name="chevRight" size={16} style={{ color: "var(--text-faint)" }} />
            </button>
            );
          })}
        </div>
      )}

      {activeTab === "discussion" && showDiscussionTab && (
        // KEY-559: read-only. A conversation always belongs to one action point, so this tab only reads —
        // each group links to the action point where its thread is answered. The structure comes from the
        // detail rather than `section`, so an action point hidden by a view filter still gets its name.
        <ScopeDiscussionTranscript
          instanceId={instanceId}
          sectionKey={section.key}
          sections={detail.sections.filter((s) => s.key === section.key)}
          meUserId={meUserId}
          onOpenAp={(_sectionKey, segmentKey) => onOpenAp(segmentKey)}
          noun={noun}
          exportTitle={transcriptTitle(detail, section.key)}
        />
      )}

      {activeTab === "changes" && compareChanges}

      {activeTab === "files" && showFilesTab && (
        // Section-scoped file browser (KEY-460 part 1): the same InstanceFilesPanel the dial-level Files
        // tab uses, handed only this section so its scope picker has nothing else to offer, and pre-opened
        // on it so the reader isn't staring at "All sections" for their own section's files.
        <InstanceFilesPanel
          instanceId={instanceId}
          sections={[section]}
          initialSectionKey={section.key}
          canDownloadDocs={canDownloadDocs}
          noun={noun}
        />
      )}

      {activeTab === "activity" && showActivityTab && (
        // The section's key alone: this section AND every action point and item under it (KEY-464).
        <ActivityPanel instanceId={instanceId} sectionKey={section.key} noun={noun} />
      )}
    </div>
  );
}

const metaLabel: CSSProperties = {
  fontSize: 12,
  color: "var(--text-faint)",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

/**
 * Overview tab: what this dial is, and nothing else (KEY-458).
 *
 * The at-a-glance strip that used to live here — type pill, "N sections · N items · N complete", four big
 * numbers and a status bar — is gone deliberately. All of it restates the dial drawn immediately to the
 * left, so it read as noise on the one tab people open to find out what a dial is *for*.
 */
function OverviewTabContent({ vm, title, scopePill, onOpenComingUp }: { vm: DialVM; title: string; scopePill?: ReactNode; /** KEY-COMINGUP: when the dial offers the Coming up tab, the strip under the title opens it. */ onOpenComingUp?: () => void }) {
  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {scopePill}
        <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-display)", lineHeight: 1.25 }}>
          {title}
        </h1>
      </div>
      {/* KEY-COMINGUP: what needs you, before anything else on the page — the counts that matter, one tap
          from the list. Absent when nothing is dated in the next week. */}
      {onOpenComingUp && <ComingUpStrip detail={vm.detail} onOpen={onOpenComingUp} />}
      {/* KEY-RESETTIME: when the dial next starts again, and for a dial admin, the day and time it does. */}
      <ResetsStrip detail={vm.detail} />
      {/* The dial-level description authored on the definition (KEY-262) — the "what is this dial for"
          copy, which until now was visible only to admins editing the definition. Mirrors how a section's
          own description renders in SectionPanel. */}
      {richTextHasContent(vm.detail.description) && (
        <RichText value={vm.detail.description} style={{ margin: "14px 0 0", color: "var(--text-muted)", fontSize: 15, lineHeight: 1.6 }} />
      )}
      {/* The dial's intro video (KEY-374), in the same slot and for the same reason as the description
          above — "what is this dial for", for people who would rather be shown than read. */}
      {vm.detail.introVideo?.playbackUrl && (
        <div style={{ marginTop: 14, maxWidth: 620 }}>
          <VideoPlayer video={vm.detail.introVideo} />
        </div>
      )}
    </div>
  );
}

/** Members tab: who owns this dial, who else is on it, and when it last moved (KEY-458 — was the second
 *  card on the Overview tab). Live mode only: a preview has no memberships to show. */
function MembersTabContent({ vm, lastUpdated }: { vm: DialVM; lastUpdated: string | null }) {
  const members = vm.detail.members.map((m) => personFrom(m));
  const ownerMember = vm.detail.members.find((m) => m.role === "Admin") ?? vm.detail.members[0] ?? null;
  const owner = ownerMember ? personFrom(ownerMember) : null;

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        {owner && (
          <div>
            <div style={metaLabel}>Owner</div>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 9 }}>
              <Avatar person={owner} size={32} ring={false} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{owner.name}</div>
                {owner.role && <div style={{ fontSize: 12.5, color: "var(--text-faint)" }}>{owner.role}</div>}
              </div>
            </div>
          </div>
        )}
        <div>
          <div style={metaLabel}>Members</div>
          <div style={{ marginTop: 11 }}>
            {members.length ? <AvatarStack people={members} size={32} /> : <span style={{ fontSize: 13.5, color: "var(--text-faint)" }}>—</span>}
          </div>
        </div>
        <div>
          <div style={metaLabel}>Last updated</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-muted)", marginTop: 11 }}>{lastUpdated ? fmtDate(lastUpdated) : "—"}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * KEY-573: a loose block of the whole-dial panel — the tab row, a list of rows or ring cards — as a padded
 * `kd-card` when the panel sits in the Dynamic workspace's frame, and as it always was otherwise.
 *
 * The frame flattens the cards among its children and grandchildren into sections of itself, and it is each
 * card's own padding that holds content off the frame's edge. A block that is not a card gets neither: the
 * WHOLE DIAL chip sat on the frame's border and the rows ran into its side. Classic has no frame, and there the
 * pill strip and the child rows float between cards by design, so they are left alone.
 */
function framedCard(framed: boolean, style: CSSProperties): { className?: string; style: CSSProperties } {
  return framed ? { className: "kd-card", style: { padding: "var(--pad-card)", ...style } } : { style };
}

/**
 * The Subdials tab of a GROUP dial (KEY-529): one row per child, and how that child is doing.
 *
 * The status is the SERVER's fold of the wedges the child feeds, not a client one — see `GroupChildRef`.
 * A child with no status yet says so in words rather than wearing an empty pill, because a pill is a claim
 * that something has been assessed.
 */
function ChildrenTabContent({ vm, onOpenChild, framed = false }: { vm: DialVM; onOpenChild?: (childInstanceId: string) => void; framed?: boolean }) {
  const children = vm.detail.children ?? [];
  if (children.length === 0) {
    return (
      <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>
          No dials have been added to this group yet.
        </p>
      </div>
    );
  }

  return (
    <div {...framedCard(framed, { display: "flex", flexDirection: "column", gap: 12 })}>
      {children.map((child) => (
        <button
          key={child.childInstanceId}
          type="button"
          className="kd-row-hover kd-focusable"
          onClick={() => onOpenChild?.(child.childInstanceId)}
          style={{
            font: "inherit",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "13px 14px",
            borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface-2)",
            cursor: "pointer",
            textAlign: "left",
            color: "var(--text)",
          }}
        >
          {/* Dial names are long and two schools in one trust routinely share a prefix, so this wraps
              rather than ellipsing — ellipsing would cut exactly the part that tells them apart. The pill
              is `flex: none` and centres against the whole block, however many lines that turns out to be. */}
          <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>{child.name}</span>
          {child.statusName ? (
            <StatusBadge colour={child.statusColour ?? RAG_COLOUR.Red} label={child.statusName} />
          ) : child.band ? (
            // No status to show, so the band is all there is — and on a band-coloured group the band IS
            // completion, which is what these three words say.
            <StatusBadge colour={RAG_COLOUR[child.band]} label={RAG_LABEL[child.band]} />
          ) : (
            <span style={{ flex: "none", fontSize: 12.5, color: "var(--text-faint)" }}>No status yet</span>
          )}
          <Icon name="chevRight" size={16} style={{ color: "var(--text-faint)" }} />
        </button>
      ))}
    </div>
  );
}

/** Dial overview panel: an Overview/Sections/Files segmented switcher over the summary and the ring cards.
 *  On a group, Sections is the Subdials tab instead (KEY-529). */
export function OverviewPanel({
  scopePill,
  framed = false,
  vm,
  instanceId,
  panelTab,
  setPanelTab,
  expanded,
  setExpanded,
  onOpenSection,
  onOpenAp,
  onOpenChild,
  onOpenChildAp,
  lastUpdated,
  hideEmpty,
  setHideEmpty,
  showEmptyToggle = false,
  showFilesTab = false,
  canDownloadDocs = true,
  showMembersTab = false,
  showActivityTab = false,
  showDiscussionTab = false,
  showComingUpTab = false,
  showLookBackTab = false,
  compareChanges,
  compareOverview,
  meUserId = null,
  aboutSlot,
  settingsSlot,
  overviewTitle,
  overviewNote,
  subs,
  onSubsChanged,
  meHasPhoneNumber,
  showSubscribe = false,
}: {
  /** KEY-532: the level chip (WHOLE DIAL / SECTION / ACTION POINT), rendered beside this panel's
   * title. In the Dynamic workspace it lives HERE rather than in a strip of its own above the
   * cards — that strip was a card in its own right, so the projection's frame and beam landed on
   * it instead of on the panel they were describing. Omitted in Classic, which keeps the strip. */
  scopePill?: ReactNode;
  /** KEY-573: the panel is mounted directly in the Dynamic workspace's projection frame, so its tab row and
   * its list are padded into cards like every other block in it (see {@link framedCard}). Off in Classic,
   * on the Settings tab (which has no frame), and for any host that mounts the panel on its own. */
  framed?: boolean;
  vm: DialVM;
  instanceId: string;
  panelTab: PanelTab;
  setPanelTab: (t: PanelTab) => void;
  expanded: Set<string>;
  setExpanded: Dispatch<SetStateAction<Set<string>>>;
  onOpenSection: (sectionKey: string) => void;
  onOpenAp: (sectionKey: string, segmentKey: string) => void;
  /** KEY-529: open a child dial from the Subdials tab. Same drill-in the wedges use, so a child opened
   *  from the list and one opened from the ring arrive the same way — dive animation included. */
  onOpenChild?: (childInstanceId: string) => void;
  /** KEY-567: open an action point inside a child dial — from a child's thread in the Discussion tab. The same
   *  drill-in again, landing on that action point. Absent where the host can't drill in. */
  onOpenChildAp?: (childInstanceId: string, sectionKey: string, segmentKey: string) => void;
  lastUpdated: string | null;
  /** "Hide empty action points" filter state; only meaningful when {@link showEmptyToggle}. */
  hideEmpty?: boolean;
  setHideEmpty?: (next: boolean) => void;
  /** Show the "Hide empty" switch (itemised dials that actually have ≥1 empty segment). */
  showEmptyToggle?: boolean;
  /** Show the Files tab (live mode only — see DialWorkspaceCapabilities.documents — AND, since KEY-537,
   * only where the dial lets this reader see that files exist at all). */
  showFilesTab?: boolean;
  /** Whether this reader may download files here (KEY-537) — gates the whole-set ZIP buttons inside the
   * Files tab. Per-row downloads are gated on each row's own `canDownload`. */
  canDownloadDocs?: boolean;
  /**
   * Show the Members tab (live mode only). Gated on the host being live rather than on
   * `members.length`, because a live dial with a single Admin still has an owner and a last-updated
   * worth showing — and a tab that appears and disappears with the data is worse than one that is
   * simply absent in preview, where `members` is always empty.
   */
  showMembersTab?: boolean;
  /** Show the Activity tab (KEY-464) — live mode only, since a draft preview has no dial to have a past. */
  showActivityTab?: boolean;
  /**
   * Show the read-only Discussion transcript (KEY-559) — live mode with messaging on only. On a group it
   * also holds what's said inside the child dials this reader can see (KEY-567).
   */
  /** KEY-COMINGUP: offer the "Coming up" tab (every dated node, soonest first). Off on a group. */
  showComingUpTab?: boolean;
  /** KEY-LOOKBACK: offer the "Look back" tab (the dial as it was). Live dials only, and not a group. */
  showLookBackTab?: boolean;
  /** Look back's compare mode is on: the strip shrinks to the compare set and this is the Changes tab. */
  compareChanges?: ReactNode;
  /** Look back's compare card (Then beside Now): shown in place of the Overview tab while comparing. */
  compareOverview?: ReactNode;
  showDiscussionTab?: boolean;
  /** The reader, so a restricted message's audience line in that transcript can say "you". */
  meUserId?: string | null;
  /**
   * The dial's own fields (KEY-434), rendered as the About tab (KEY-485). Host-supplied and optional:
   * absent means no About tab at all, which is what the great majority of dials get, because they
   * declare no dial-level fields. Until now this block rendered *below* the tab strip, so it sat under
   * whichever tab happened to be open — Sections, Files, Members alike.
   */
  aboutSlot?: ReactNode;
  /**
   * The dial's settings, rendered as the last tab (KEY-455 continued). Host-supplied, because every
   * setting behind it — members, guests, feeds, status roles, structure — is a write this package has no
   * business knowing about; it only decides where they live. Absent means no Settings tab at all, which
   * is what a Reader and the admin's draft preview both get.
   */
  settingsSlot?: ReactNode;
  /** Heading for the Overview tab. Host-supplied: the definition's name lives on InstanceSummary, which
   *  only the host holds — `InstanceDetail` has `definitionId` but no name. Defaults to the dial's name. */
  overviewTitle?: string;
  /** KEY-737: shown above the Overview's own content. */
  overviewNote?: ReactNode;
  /** Threaded through to the icon-only subscribe control on each section/action-point card (KEY-461) —
   *  same shape SectionPanel already takes for its own full-size button. */
  subs?: NotificationSubscriptionDto[];
  onSubsChanged?: () => void | Promise<void>;
  meHasPhoneNumber?: boolean;
  /** Show the card subscribe icons (live mode only — see DialWorkspaceCapabilities.mode). */
  showSubscribe?: boolean;
}) {
  // A dial whose fields all become invisible mid-session (an access change) would otherwise strand the
  // viewer on a tab that no longer has a strip entry. Normalise for display only; the stored tab is left
  // alone so it comes back if the fields do.
  //
  // KEY-529 adds a second case, and it is not an edge: the panel OPENS on "sections", so without this a
  // group would land on a tab its own strip does not offer. The two are the same slot under two names, so
  // they map onto each other in both directions — which also means walking from a group to a child, or
  // back, keeps you on the equivalent tab rather than throwing you to Overview.
  const activeTab: PanelTab =
    compareChanges && !COMPARE_TAB_IDS.has(panelTab) ? "overview"
    : !compareChanges && panelTab === "changes" ? "overview"
    : panelTab === "about" && !aboutSlot ? "overview"
    : panelTab === "comingUp" && !showComingUpTab ? "overview"
    : panelTab === "lookBack" && !showLookBackTab ? "overview"
    : panelTab === "discussion" && !showDiscussionTab ? "overview"
    : vm.isGroup && panelTab === "sections" ? "children"
    : !vm.isGroup && panelTab === "children" ? "sections"
    : panelTab;

  // KEY-547: the section/action-point list is ONE slot under two names — KEY-529 renamed it "Subdials" on
  // a group — and the "Hide empty" switch belongs to that slot, not to the word "sections". Gated on the
  // literal it could not render on a group at all, because `activeTab` is never "sections" there: the
  // switch was reachable from no tab whatsoever, which is how a built feature came to be unreachable.
  // Name the slot once and compare against the name.
  const listTab: PanelTab = vm.isGroup ? "children" : "sections";

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap-stack)" }}>
      <div {...framedCard(framed, { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" })}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap" }}>
          {/* In the Dynamic workspace the level chip lives inside the panel's own card (KEY-532) — but at
              dial level that card is the OVERVIEW tab's, and the panel opens on Sections. So on the landing
              screen, which is where "which level am I on?" is least obvious, nothing said it at all. Shown
              here on every other tab, so the chip is genuinely always on screen: one at a time, never two. */}
          {activeTab !== "overview" && scopePill}
          <PanelTabs tab={activeTab} setTab={setPanelTab} labels={vm.detail} showAbout={!!aboutSlot} showChildren={vm.isGroup} childrenLabel={listTabLabel(vm.detail)} showComingUp={showComingUpTab} showLookBack={showLookBackTab} showDiscussion={showDiscussionTab} showFiles={showFilesTab} showMembers={showMembersTab} showActivity={showActivityTab} showSettings={!!settingsSlot} compare={!!compareChanges} />
        </div>
        {/* The "Hide empty" switch filters the section/segment list, so it belongs to that tab alone.
            Stated positively rather than as a list of exceptions — as an exception list it silently
            leaked onto every tab added after it (Members here, Activity next). */}
        {showEmptyToggle && setHideEmpty && activeTab === listTab && <EmptyToggle on={!!hideEmpty} onChange={setHideEmpty} noun={segmentNoun(vm.detail)} />}
      </div>
      {activeTab === "overview" && compareOverview ? (
        compareOverview
      ) : activeTab === "overview" ? (
        <>
          {overviewNote}
          <OverviewTabContent vm={vm} title={overviewTitle?.trim() || vm.detail.name} scopePill={scopePill} onOpenComingUp={showComingUpTab ? () => setPanelTab("comingUp") : undefined} />
        </>
      ) : activeTab === "about" ? (
        aboutSlot
      ) : activeTab === "guidance" ? (
        // KEY-534: how to work the whole dial — the level the three below it always had and this one
        // didn't, though the column has been there from the start. Same shape as the section's and the
        // action point's Guidance tab, down to the empty state.
        <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 14 }}>
          <Prose text={vm.detail.guidance ?? ""} fallback="No guidance added yet." muted />
          {vm.detail.guidanceVideo && <VideoPlayer video={vm.detail.guidanceVideo} />}
          {/* KEY-565: and the documents and links that go with it, in the order the section's tab uses. */}
          <GuidanceDocuments documents={vm.detail.guidanceDocuments} />
          <ExternalResources links={vm.detail.guidanceLinks} />
        </div>
      ) : activeTab === "children" ? (
        <ChildrenTabContent vm={vm} onOpenChild={onOpenChild} framed={framed} />
      ) : activeTab === "discussion" ? (
        // KEY-559: everything said on this dial that the reader can see, read-only. `vm.detail.sections`
        // rather than `vm.sections`: the latter may be the hide-empty view, which would strip the names off
        // the very action points a conversation was held on. On a group it includes the children (KEY-567),
        // whose structure arrives with the transcript itself.
        <ScopeDiscussionTranscript
          instanceId={instanceId}
          sections={vm.detail.sections}
          meUserId={meUserId}
          onOpenAp={onOpenAp}
          onOpenChildAp={onOpenChildAp}
          noun={segmentNoun(vm.detail)}
          isGroup={vm.isGroup}
          exportTitle={transcriptTitle(vm.detail)}
        />
      ) : activeTab === "files" ? (
        // KEY-643: on a group the tab rolls up every subdial's files too, as Discussion does their messages.
        <InstanceFilesPanel instanceId={instanceId} sections={vm.sections} canDownloadDocs={canDownloadDocs} noun={segmentNoun(vm.detail)} isGroup={vm.isGroup} />
      ) : activeTab === "members" ? (
        <MembersTabContent vm={vm} lastUpdated={lastUpdated} />
      ) : activeTab === "comingUp" ? (
        <ComingUpPanel detail={vm.detail} noun={segmentNoun(vm.detail)} onOpen={onOpenAp} />
      ) : activeTab === "lookBack" ? (
        <LookBackPanel detail={vm.detail} noun={segmentNoun(vm.detail)} />
      ) : activeTab === "changes" ? (
        compareChanges
      ) : activeTab === "activity" ? (
        // No keys: the whole dial, every section and everything under them (KEY-464).
        <ActivityPanel instanceId={instanceId} noun={segmentNoun(vm.detail)} />
      ) : activeTab === "settings" ? (
        settingsSlot
      ) : (
        // In the frame the ring cards sit one level inside this padded card, so they stay cards of their own.
        <div {...framedCard(framed, { display: "flex", flexDirection: "column", gap: 12 })}>
          {vm.sections.map((section) => (
            <SectionRingCard
              noun={segmentNoun(vm.detail, section.key)}
              key={section.key}
              section={section}
              expanded={expanded.has(section.key)}
              onToggle={() => toggle(section.key)}
              onOpenSection={() => onOpenSection(section.key)}
              onOpenAp={(segmentKey) => onOpenAp(section.key, segmentKey)}
              instanceId={instanceId}
              subs={subs}
              onSubsChanged={onSubsChanged}
              meHasPhoneNumber={meHasPhoneNumber}
              showSubscribe={showSubscribe}
            />
          ))}
        </div>
      )}
    </div>
  );
}
